import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import { api, API_BASE } from "@/lib/api";
import { tokenStore } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Download, Upload, Trash2, FileSpreadsheet, CheckCircle2, AlertTriangle, Info, CalendarRange, Filter, MapPin, Users, XCircle, CalendarOff } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/utils-app";
import { StatTile, StatStrip } from "@/components/StatTile";

function istMonth() {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}`;
}

function codeStyle(code) {
  switch (code) {
    case "P":
      return "bg-emerald-500 text-white";
    case "A":
      return "bg-rose-200 text-rose-900";
    case "H":
      return "bg-violet-500 text-white";
    default:
      return "bg-secondary text-muted-foreground";
  }
}

export default function AttendanceCalendarPage() {
  const { user } = useAuth();
  // Contractor Admin only ever sees their own contractor's data — the
  // backend force-overrides this regardless, but hide the PMC/contractor
  // toggle too so the UI doesn't imply a choice that isn't actually honoured.
  const isAdmin = user?.role === "admin";
  const [parks, setParks] = useState([]);
  const [parkId, setParkId] = useState("");
  const [month, setMonth] = useState(istMonth());
  const [role, setRole] = useState(isAdmin ? "all" : "contractor_supervisor");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/parks").then((r) => {
      setParks(r.data);
      if (!parkId && r.data[0]) setParkId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const reload = () => {
    if (!parkId || !month) return;
    setBusy(true);
    const params = { park_id: parkId, month };
    if (role !== "all") params.role = role;
    api
      .get("/admin/attendance/calendar", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [parkId, month, role]);

  const dayHeaders = useMemo(() => (data?.days || []).map((d) => d.split("-")[2]), [data]);

  const downloadXlsx = async () => {
    try {
      const params = new URLSearchParams({ park_id: parkId, month });
      if (role !== "all") params.set("role", role);
      const url = `${API_BASE}/admin/attendance/export.xlsx?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `attendance_${data?.park?.name?.replace(/\s+/g, "_") || "export"}_${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Download failed");
    }
  };

  const setOverride = async (employeeId, date, code) => {
    try {
      await api.post("/admin/attendance/override", { employee_id: employeeId, date, code });
      toast.success(code ? `Marked ${code === "P" ? "Present" : "Absent"}` : "Override cleared");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const setSupervisorOverride = async (supervisorId, date, code) => {
    try {
      await api.post("/admin/supervisor-attendance/override", { supervisor_id: supervisorId, date, code });
      toast.success(code ? `Marked supervisor ${code === "P" ? "Present" : "Absent"}` : "Override cleared");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // Compute totals across all worker rows (exclude supervisor rows)
  const totals = (data?.rows || []).reduce(
    (acc, r) => {
      if (r.is_supervisor) return acc;
      acc.workers += 1;
      acc.present += r.present || 0;
      acc.absent += r.absent || 0;
      acc.holidays += r.holidays || 0;
      for (const c of r.cells || []) {
        if (c.con_recon === "mismatch") acc.mismatches += 1;
      }
      return acc;
    },
    { workers: 0, present: 0, absent: 0, holidays: 0, mismatches: 0 },
  );
  const park = parks.find((p) => p.id === parkId);

  return (
    <AppShell title="Monthly Calendar" subtitle="PMC captures drive P/A · contractor captures used for reconciliation · click any cell to override">
      <div className="flex flex-col gap-4">
        {/* Filter bar */}
        <Card>
          <CardContent className="grid gap-3 p-4 md:grid-cols-12">
            <div className="md:col-span-5 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <MapPin className="mr-1 inline h-3 w-3" /> Park
              </Label>
              <Select value={parkId} onValueChange={setParkId}>
                <SelectTrigger data-testid="cal-park-select" className="h-10"><SelectValue placeholder="Select park" /></SelectTrigger>
                <SelectContent>
                  {parks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarRange className="mr-1 inline h-3 w-3" /> Month
              </Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="cal-month-input" className="h-10" />
            </div>
            {isAdmin && (
              <div className="md:col-span-2 flex flex-col gap-1.5">
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <Filter className="mr-1 inline h-3 w-3" /> Source
                </Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="cal-role-select" className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">PMC (default)</SelectItem>
                    <SelectItem value="pmc_supervisor">PMC only</SelectItem>
                    <SelectItem value="contractor_supervisor">Contractor only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground opacity-0">action</Label>
              <Button onClick={downloadXlsx} disabled={!data} data-testid="cal-download-xlsx" className="h-10">
                <Download className="mr-1.5 h-4 w-4" /> Excel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary tiles */}
        <StatStrip cols={5}>
          <StatTile icon={Users} accent="slate" label="Workers" value={totals.workers} hint={park?.name} testid="cal-stat-workers" />
          <StatTile icon={CheckCircle2} accent="emerald" label="Present (P)" value={totals.present} testid="cal-stat-present" />
          <StatTile icon={XCircle} accent="rose" label="Absent (A)" value={totals.absent} testid="cal-stat-absent" />
          <StatTile icon={CalendarOff} accent="violet" label="Holidays (H)" value={totals.holidays} testid="cal-stat-holidays" />
          <StatTile icon={AlertTriangle} accent="amber" label="PMC↔Con Mismatch" value={totals.mismatches} hint="Cross-role discrepancies" testid="cal-stat-mismatches" />
        </StatStrip>

        <BiometricSection parkId={parkId} onUploaded={reload} />

        {busy ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : !data ? null : data.rows.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">No employees in this park.</CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="sticky top-0 z-10 bg-card">
                    <th className="sticky left-0 z-20 bg-card px-3 py-2 text-left font-medium">Employee</th>
                    {dayHeaders.map((d, i) => (
                      <th key={i} className="px-1 py-2 text-center font-medium">{d}</th>
                    ))}
                    <th className="px-2 py-2 text-center font-medium border-l border-l-border bg-emerald-50/40">P</th>
                    <th className="px-2 py-2 text-center font-medium bg-rose-50/40">A</th>
                    <th className="px-2 py-2 text-center font-medium bg-violet-50/40">H</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.employee_id} className={`border-t ${r.is_supervisor ? "bg-amber-50/40" : ""}`} data-testid={`cal-row-${r.employee_code}`}>
                      <td className={`sticky left-0 z-10 px-3 py-1.5 ${r.is_supervisor ? "bg-amber-50/40" : "bg-card"}`}>
                        <div className="font-medium leading-tight flex items-center gap-1.5">
                          {r.employee_name}
                          {r.is_supervisor && (
                            <span className="text-[10px] font-medium uppercase rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 tracking-wider">
                              Supervisor
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                          <span className="font-mono">{r.employee_code}</span>
                          {r.designation ? (
                            <>
                              <span aria-hidden>·</span>
                              <span data-testid={`cal-row-${r.employee_code}-designation`}>{r.designation}</span>
                            </>
                          ) : null}
                        </div>
                      </td>
                      {r.cells.map((c) => (
                        <td key={c.date} className="px-1 py-1">
                          <CellPopover
                            row={r}
                            cell={c}
                            disabled={false}
                            onChoose={(code) =>
                              r.is_supervisor
                                ? setSupervisorOverride(r.employee_id, c.date, code)
                                : setOverride(r.employee_id, c.date, code)
                            }
                          />
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-center font-semibold text-emerald-700 border-l border-l-border bg-emerald-50/40">{r.present}</td>
                      <td className="px-2 py-1.5 text-center font-semibold text-rose-700 bg-rose-50/40">{r.absent}</td>
                      <td className="px-2 py-1.5 text-center font-semibold text-violet-700 bg-violet-50/40">{r.holidays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-500" /> P · Present</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-200" /> A · Absent</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-violet-500" /> H · Holiday (counted as Present)</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-secondary text-muted-foreground inline-flex items-center justify-center text-[10px]">—</span> Not marked (no captures that day)</span>
          <span className="mx-2 h-3 w-px bg-border" />
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full ring-2 ring-emerald-500 ring-offset-1" /> Matches biometric</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full ring-2 ring-rose-500 ring-offset-1" /> Biometric mismatch</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full ring-2 ring-amber-500 ring-offset-1" /> PMC ↔ Contractor mismatch</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> Manually overridden</span>
        </div>
      </div>
    </AppShell>
  );
}

function CellPopover({ row, cell, disabled, onChoose }) {
  const recon = cell.recon; // 'match' | 'mismatch' | null (biometric)
  const conRecon = cell.con_recon; // 'match' | 'mismatch' | null (PMC vs contractor)
  const isPast = (() => {
    const today = new Date();
    const tz = today.getTimezoneOffset() * 60000;
    return cell.date <= new Date(today.getTime() - tz).toISOString().slice(0, 10);
  })();
  // Priority for visible ring:
  //   1) biometric mismatch (rose) — already a strong signal from admin upload
  //   2) PMC vs Contractor mismatch (amber)
  //   3) biometric match (emerald)
  let ring = "";
  if (recon === "mismatch") ring = "ring-2 ring-rose-500 ring-offset-1";
  else if (conRecon === "mismatch") ring = "ring-2 ring-amber-500 ring-offset-1";
  else if (recon === "match") ring = "ring-2 ring-emerald-500 ring-offset-1";

  const isFuture = (() => {
    const today = new Date();
    const tz = today.getTimezoneOffset() * 60000;
    const todayISOStr = new Date(today.getTime() - tz).toISOString().slice(0, 10);
    return cell.date > todayISOStr;
  })();
  // Cell label: "·" for future dates, "—" (em dash) for past dates where no
  // one used the system in this park, otherwise the actual P/A/H code.
  const cellLabel = cell.code || (isFuture ? "·" : "—");

  const Cell = (
    <button
      type="button"
      className={`mx-auto relative flex h-7 w-7 items-center justify-center rounded text-[11px] font-semibold ${codeStyle(cell.code)} ${ring} disabled:cursor-default`}
      title={[
        `${fmtDate(cell.date)} · ${cell.code || (isFuture ? "future" : "not marked")}`,
        cell.override ? "Manual override" : null,
        cell.biom ? `Biometric: ${cell.biom}` : null,
        recon === "match" ? "Matches biometric" : recon === "mismatch" ? "Differs from biometric" : null,
        conRecon === "mismatch" ? `PMC vs Contractor mismatch (PMC=${cell.pmc_present ? "P" : "A"}, Contractor=${cell.con_present ? "P" : "A"})` : null,
      ].filter(Boolean).join(" · ")}
      disabled={disabled}
      data-testid={`cal-cell-${row.employee_code}-${cell.date}`}
    >
      {cellLabel}
      {cell.override && (
        <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-amber-500 ring-1 ring-white" />
      )}
    </button>
  );

  if (disabled) return Cell;

  return (
    <Popover>
      <PopoverTrigger asChild>{Cell}</PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="center">
        <div className="mb-2 text-xs">
          <div className="font-medium">{fmtDate(cell.date)} · {row.employee_name}</div>
          <div className="text-muted-foreground">
            System: <b>{cell.code || "—"}</b>
            {cell.override ? <> · <span className="text-amber-600">overridden</span></> : null}
            {cell.biom ? <> · biometric: <b>{cell.biom}</b></> : null}
            {recon === "match" ? <> · <span className="text-emerald-600">match</span></> : recon === "mismatch" ? <> · <span className="text-rose-600">mismatch</span></> : null}
          </div>
          <div className="mt-1 text-[11px]">
            <span className={cell.pmc_present ? "text-emerald-700" : "text-muted-foreground"}>PMC: {cell.pmc_present ? "P" : "A"}</span>
            <span className="mx-1">·</span>
            <span className={cell.con_present ? "text-emerald-700" : "text-muted-foreground"}>Contractor: {cell.con_present ? "P" : "A"}</span>
            {conRecon === "mismatch" && (
              <span className="ml-1 inline-flex items-center gap-1 text-amber-700">
                <AlertTriangle className="h-3 w-3" /> mismatch
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <Button size="sm" variant={cell.code === "P" ? "default" : "outline"} onClick={() => onChoose("P")} data-testid={`cal-set-P-${row.employee_code}-${cell.date}`}>
            Present
          </Button>
          <Button size="sm" variant={cell.code === "A" ? "default" : "outline"} onClick={() => onChoose("A")} data-testid={`cal-set-A-${row.employee_code}-${cell.date}`}>
            Absent
          </Button>
        </div>
        {cell.override && (
          <Button size="sm" variant="ghost" className="mt-2 w-full text-xs" onClick={() => onChoose(null)} data-testid={`cal-set-auto-${row.employee_code}-${cell.date}`}>
            Clear override (auto)
          </Button>
        )}
        {!isPast && (
          <div className="mt-1 text-[10px] text-muted-foreground">Tip: overrides apply to past or current dates.</div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function BiometricSection({ parkId, onUploaded }) {
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = () => api.get("/admin/biometric/uploads").then((r) => setUploads(r.data || [])).catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (parkId) fd.append("park_id", parkId);
      const { data } = await api.post("/admin/biometric/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.rows_imported} rows${data.rows_skipped ? `, skipped ${data.rows_skipped}` : ""}`);
      refresh();
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (uid) => {
    if (!window.confirm("Remove this biometric batch? Reconciliation badges will disappear for those dates.")) return;
    try {
      await api.delete(`/admin/biometric/uploads/${uid}`);
      refresh();
      onUploaded?.();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <Card data-testid="biom-section">
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-primary" />
          <span className="font-medium text-sm">Biometric reconciliation</span>
          <span className="text-xs text-muted-foreground">
            Upload the contractor's biometric attendance export. We'll compare it against our captures and badge each cell {" "}
            <CheckCircle2 className="inline h-3 w-3 text-emerald-600" /> match or <AlertTriangle className="inline h-3 w-3 text-rose-600" /> mismatch.
          </span>
          <label className="ml-auto inline-flex">
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.txt"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload(f);
                e.target.value = "";
              }}
              data-testid="biom-upload-input"
            />
            <Button size="sm" disabled={busy} asChild={false} onClick={(e) => e.currentTarget.previousSibling.click()}>
              <Upload className="mr-1.5 h-4 w-4" /> {busy ? "Uploading…" : "Upload file"}
            </Button>
          </label>
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} data-testid="biom-format-toggle">
            <Info className="mr-1.5 h-4 w-4" /> Format
          </Button>
        </div>

        {open && (
          <div className="rounded-md border bg-secondary/30 p-3 text-xs">
            <div className="mb-1 font-medium">Expected file (CSV or Excel)</div>
            <div className="font-mono leading-relaxed">
              employee_code, date, status, check_in, check_out<br />
              EMP001, 2026-05-08, P, 09:02, 17:30<br />
              EMP001, 2026-05-09, A, ,<br />
              EMP002, 08/05/2026, Present, , <br />
              EMP003, 09-05-2026, 1, ,
            </div>
            <ul className="mt-2 list-disc pl-5 text-muted-foreground">
              <li><b>employee_code</b> must match the worker code in the system.</li>
              <li><b>date</b> accepts <code>yyyy-mm-dd</code>, <code>dd/mm/yyyy</code>, <code>dd-mm-yyyy</code>, <code>dd.mm.yyyy</code>.</li>
              <li><b>status</b>: P / A / Present / Absent / 1 / 0.</li>
              <li><b>check_in</b>, <b>check_out</b> optional.</li>
              <li>Re-uploading the same employee+date overwrites the previous biometric entry.</li>
            </ul>
          </div>
        )}

        {uploads.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {uploads.slice(0, 5).map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-2 text-xs rounded-md border bg-card px-2 py-1.5">
                <FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">{u.file_name || u.id.slice(0, 8)}</span>
                <Badge variant="secondary" className="text-[10px]">{u.rows_imported} rows</Badge>
                {u.date_min && u.date_max && (
                  <span className="text-muted-foreground">{fmtDate(u.date_min)} → {fmtDate(u.date_max)}</span>
                )}
                <span className="text-muted-foreground">· uploaded {fmtDateTime(u.created_at)}</span>
                <span className="text-muted-foreground">by {u.uploaded_by_name}</span>
                <Button size="icon" variant="ghost" className="ml-auto h-6 w-6" onClick={() => remove(u.id)} aria-label="Remove">
                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
